/**
 * chatHarnessStore — Plan E Phase 3 v1.1.0
 *
 * Per-tenant/user chat harness selection. Replaces the build-time
 * VITE_USE_PI_CHAT toggle with a runtime, user-controlled choice persisted
 * to localStorage. Designed to grow when more harnesses are added (Goose
 * web UI, hand-rolled, etc.) — add a value to ChatHarnessId,
 * update the selector + SettingsMenu dropdown.
 *
 * Storage key: `vibe_chat_harness_${tenantId}` so each tenant a user visits
 * has its own selection. localStorage is per-browser-per-user, which gives
 * us per-user-per-tenant scoping with zero backend work. A future
 * enhancement is a per-tenant DB-backed default that overrides when the
 * user has not made a selection — out of scope here.
 *
 * Default value (1.569.0): "pi" — a fresh browser (no stored selection) opens
 * straight into the Pi agent harness (tools + artifacts) so users land on the
 * agentic chat by default. Was "legacy" through 1.568.0; users who already
 * picked a harness keep their stored choice (readStored wins over the default).
 */
import { create } from 'zustand';

export type ChatHarnessId = 'pi' | 'legacy' | 'none';

const VALID_HARNESSES: readonly ChatHarnessId[] = ['pi', 'legacy', 'none'];

const DEFAULT_HARNESS: ChatHarnessId = 'pi';

const STORAGE_KEY_PREFIX = 'vibe_chat_harness_';

function storageKeyFor(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStored(tenantId: string | null): ChatHarnessId {
  if (!tenantId) return DEFAULT_HARNESS;
  try {
    const raw = localStorage.getItem(storageKeyFor(tenantId));
    if (raw && (VALID_HARNESSES as readonly string[]).includes(raw)) {
      return raw as ChatHarnessId;
    }
  } catch {
    // localStorage unavailable (private mode, jsdom edge cases) — fall back.
  }
  return DEFAULT_HARNESS;
}

function writeStored(tenantId: string, harness: ChatHarnessId): void {
  try {
    localStorage.setItem(storageKeyFor(tenantId), harness);
  } catch {
    // Ignore quota / disabled-storage failures — runtime stays correct.
  }
}

interface ChatHarnessState {
  harness: ChatHarnessId;
  /** Hydrate from localStorage given the tenant id (called from App on mount). */
  hydrate: (tenantId: string | null) => void;
  /** Update the harness selection + persist for the given tenant. */
  setHarness: (tenantId: string, harness: ChatHarnessId) => void;
}

export const useChatHarnessStore = create<ChatHarnessState>((set) => ({
  harness: DEFAULT_HARNESS,
  hydrate: (tenantId) => set({ harness: readStored(tenantId) }),
  setHarness: (tenantId, harness) => {
    writeStored(tenantId, harness);
    set({ harness });
  },
}));

export const CHAT_HARNESS_LABELS: Record<ChatHarnessId, string> = {
  pi: 'Pi (Mario Zechner — agent harness with tools + artifacts)',
  legacy: 'Legacy (vibe-designer SSE chat with project context)',
  none: 'None (chat panel hidden)',
};
